import { Box, Card, CardContent, Grid, Paper, Typography } from '@mui/material';
import { Link, navigate } from '@reach/router';
import React from 'react';
import Layout from '../components/Layout';
import LeadStats from '../components/LeadStats';
import QuickLeadCreate from '../components/QuickLeadCreate';
import { useAuth } from '../contexts/AuthContext';
import { useRealtimeSSE } from '../hooks/useRealtimeSSE';

const DashboardPage = () => {
  const { user, loading: authLoading } = useAuth();

  // ✅ Подключение к SSE для real-time обновлений
  useRealtimeSSE();

  React.useEffect(() => {
    if (!authLoading && !user) {
      navigate('/auth');
    }
  }, [authLoading, user]);

  if (authLoading || !user) {
    return null;
  }

  return (
    <Layout>
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
            <Typography variant="h4" component="h1" gutterBottom>
              🏠 Добро пожаловать, {user.name}!
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Управляйте своим бизнесом в одном месте
            </Typography>
          </Box>
          <QuickLeadCreate showAsFab={false} />
        </Box>
      </Box>

      {/* Статистика лидов */}
      <Card sx={{
        mb: 4,
        background: 'rgba(255, 255, 255, 0.1)',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(255, 255, 255, 0.2)'
      }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            📥 Лиды
          </Typography>
          <LeadStats compact={true} />
        </CardContent>
      </Card>

      <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
        🚀 Быстрые действия
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6} lg={3}>
          <Paper
            component={Link}
            to="/leads"
            sx={{
              p: 3,
              height: '100%',
              textDecoration: 'none',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
              background: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              '&:hover': {
                background: 'rgba(255, 255, 255, 0.15)',
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)'
              },
              transition: 'all 0.3s ease'
            }}
          >
            <Typography variant="h3" sx={{ mb: 1 }}>📥</Typography>
            <Typography variant="h6" gutterBottom>
              Лиды
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Управляйте потенциальными клиентами
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6} lg={3}>
          <Paper
            component={Link}
            to="/clients"
            sx={{
              p: 3,
              height: '100%',
              textDecoration: 'none',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
              background: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              '&:hover': {
                background: 'rgba(255, 255, 255, 0.15)',
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)'
              },
              transition: 'all 0.3s ease'
            }}
          >
            <Typography variant="h3" sx={{ mb: 1 }}>👥</Typography>
            <Typography variant="h6" gutterBottom>
              Клиенты
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Управляйте базой клиентов
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6} lg={3}>
          <Paper
            component={Link}
            to="/products"
            sx={{
              p: 3,
              height: '100%',
              textDecoration: 'none',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
              background: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              '&:hover': {
                background: 'rgba(255, 255, 255, 0.15)',
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)'
              },
              transition: 'all 0.3s ease'
            }}
          >
            <Typography variant="h3" sx={{ mb: 1 }}>📦</Typography>
            <Typography variant="h6" gutterBottom>
              Товары
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Управляйте каталогом товаров
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6} lg={3}>
          <Paper
            component={Link}
            to="/deals"
            sx={{
              p: 3,
              height: '100%',
              textDecoration: 'none',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
              background: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              '&:hover': {
                background: 'rgba(255, 255, 255, 0.15)',
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)'
              },
              transition: 'all 0.3s ease'
            }}
          >
            <Typography variant="h3" sx={{ mb: 1 }}>💼</Typography>
            <Typography variant="h6" gutterBottom>
              Сделки
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Отслеживайте сделки и платежи
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Floating Action Button */}
      <QuickLeadCreate showAsFab={true} />
    </Layout>
  );
};

export default DashboardPage; 